import React from 'react'
import { Item, ItemParams, Menu, useContextMenu } from 'react-contexify'
import 'react-contexify/dist/ReactContexify.css'
import ReactECharts from 'src/components/echarts'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import {
    LifesHubCard,
    LifesHubCardBody,
    LifesHubCardHeader,
    LifesHubEmptyState,
    ModuleView,
    PageHead,
    PageSubtitle,
    PageTitle,
    SkeletonBlock
} from 'src/utils/views'

import { TheConnectionScope } from '../tc_scopes'
import HoverBarView from './tc_hover-bar_view'
import ListagemTabsView from './tc_listagem_tabs_view'
import SelectedNodesView from './tc_selected-nodes_view'

import type { ECharts } from 'echarts'
import ConfirmExportDataLimit from 'src/main/views/components/ConfirmExportDataLimit'
import type { GraphNode } from '../tc_types'

const LOG = Logger.get('TC-VIEW')

const PageView = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minWidth: 0,
    gap: 10,
    padding: '26px 28px 42px',
    overflowX: 'hidden',
    overflowY: 'auto',
    '@media (max-width: 900px)': {
        padding: '22px 18px 36px'
    }
})

const NodeMenu = styled(Menu)({
    '--contexify-zIndex': 500,
    '--contexify-menu-minWidth': '190px',
    '--contexify-menu-padding': '7px',
    '--contexify-menu-radius': '10px',
    '--contexify-menu-bgColor': 'var(--surface)',
    '--contexify-menu-shadow': 'var(--shadow-2)',
    '--contexify-item-color': 'var(--ink)',
    '--contexify-itemContent-padding': '9px 10px',
    '--contexify-activeItem-radius': '7px',
    '--contexify-activeItem-color': 'var(--green-800)',
    '--contexify-activeItem-bgColor': 'var(--green-050)',
    '--contexify-separator-color': 'var(--border)',
    border: '1px solid var(--border)',
    fontFamily: 'var(--font)',
    fontSize: 14,
    fontWeight: 650
})

const useStyles = makeStyles()({
    graphCard: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        minHeight: 420
    },
    chartArea: {
        position: 'relative',
        display: 'flex',
        flexGrow: 1,
        minHeight: 0
    },
    emptyOverlay: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none'
    },
    chart: {
        display: 'flex',
        flexGrow: 1
    },
    hoverBar: {
        position: 'absolute',
        right: 8,
        bottom: 8
    },
    appliedFilters: {
        flexShrink: 0,
        paddingBottom: 10
    },
    tablesPane: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        minHeight: 440
    },
    skeletonGraph: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        minHeight: 420
    },
    skeletonTables: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        minHeight: 440
    },
    emptyCard: {
        flexGrow: 1,
        minHeight: 420
    }
})

type GraphCompaniesViewProps = {
    className?: string
    scope: TheConnectionScope
}

const MENU_ID = '380a0d88-9dca-4b1d-867a-e2319d1775b7'

// https://github.com/fkhadra/react-contexify

export const TheConnectionView = function ({ className, scope }: GraphCompaniesViewProps) {
    bindUpdate(React, scope)

    const { show } = useContextMenu({
        id: MENU_ID
    })

    const instanceHolder = React.useMemo(() => ({ instance: undefined as ECharts | undefined }), [])

    const handleExclusiveFilterClick = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ event, props }: ItemParams<Record<string, unknown>, Record<string, unknown>>) => {
            // console.log(event, props)
            if (props) {
                const node = props.node as GraphNode
                scope.onNodeClicked(node, true).catch(LOG.caught)
            }
        },
        [scope]
    )

    const handleAdditionalFilterClick = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ event, props }: ItemParams<Record<string, unknown>, Record<string, unknown>>) => {
            // console.log(event, props)
            if (props) {
                const node = props.node as GraphNode
                scope.onNodeClicked(node, false).catch(LOG.caught)
            }
        },
        [scope]
    )

    const handleCopyInformationalDataClick = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ event, props }: ItemParams<Record<string, unknown>, Record<string, unknown>>) => {
            // console.log(event, props)
            if (props) {
                const node = props.node as GraphNode
                scope.onCopyInformationalData(node).catch(LOG.caught)
            }
        },
        [scope]
    )

    const handleCopyIdentityDataClick = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ event, props }: ItemParams<Record<string, unknown>, Record<string, unknown>>) => {
            // console.log(event, props)
            if (props) {
                const node = props.node as GraphNode
                scope.onCopyIdentityData(node).catch(LOG.caught)
            }
        },
        [scope]
    )

    const handleChartReady = React.useCallback(
        (instance: ECharts) => {
            instanceHolder.instance = instance

            const getSeriesByIndex = (idx: number) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const anyEChart = instance as unknown as any

                const model = anyEChart.getModel()
                if (model) {
                    return model.getSeriesByIndex(idx)
                }
            }

            scope.refreshChart = () => {
                instance.resize({ height: 'auto', width: 'auto' })
            }

            scope.getZoomValue = () => {
                const serie = getSeriesByIndex(0)
                if (serie) {
                    return serie.coordinateSystem.getZoom()
                }
                return 0.2
            }

            scope.scaleZoom = (scale) => {
                echartZoom(instance, scale)
            }

            scope.mergeSerieOptions = (options) => {
                const serie = getSeriesByIndex(0)
                if (!serie) {
                    return {}
                }
                serie.mergeOption(options)
            }

            instance.on('contextmenu', { dataType: 'node' }, function (this, item) {
                // call this method while the node of graph was clicked.
                //console.log('contextmenu on node:', item)
                //console.log('echart', instance)

                const rawEvent = item.event?.event

                if (rawEvent) {
                    show({
                        event: rawEvent,
                        props: {
                            node: item.data
                        }
                    })
                }
            })

            instance.on('click', { dataType: 'edge' }, function (this, item) {
                // call this method while the edge of graph was clicked.
                console.log('click on edge: ', item)
            })

            instance.on('click', { dataType: 'node' }, function (this, item) {
                // call this method while the edge of graph was clicked.
                console.log('click on node: ', item.data)
            })

            instance.on('dblclick', { dataType: 'node' }, function (this, item) {
                const node = item.data as unknown as GraphNode
                scope.onNodeClicked(node, true).catch(LOG.caught)
            })

            instance.on('finished', function () {
                // NOOP
            })

            scope.onReady().catch(LOG.caught)
        },
        [scope]
    )

    const { classes } = useStyles()

    const pageHead = (
        <PageHead>
            <div>
                <PageTitle>Relações Societárias</PageTitle>
                <PageSubtitle>
                    Explore os vínculos entre empresas, sócios e pessoas a partir de uma busca — o grafo desenha a rede
                    encontrada e as listagens detalham o recorte.
                </PageSubtitle>
            </div>
        </PageHead>
    )

    let content: React.ReactNode

    if (scope.waiting) {
        content = (
            <>
                <SkeletonBlock className={classes.skeletonGraph} />
                <SkeletonBlock className={classes.skeletonTables} />
            </>
        )
    } else if (!scope.options) {
        content = (
            <LifesHubCard className={classes.emptyCard} elevation={0}>
                <LifesHubEmptyState
                    title="Nenhuma rede para mostrar"
                    text="Busque uma empresa, um sócio ou uma pessoa na barra superior para desenhar as relações societárias encontradas."
                />
            </LifesHubCard>
        )
    } else {
        const showTables = scope.tablesVisible && scope.maximizeCard !== 'graph'
        const showGraph = !(showTables && scope.maximizeCard === 'tables')

        content = (
            <>
                {showGraph && (
                    <LifesHubCard className={classes.graphCard} elevation={0}>
                        <LifesHubCardHeader title="Rede de relações societárias" />
                        <LifesHubCardBody>
                            {scope.selectedNodesPanel && (
                                <SelectedNodesView
                                    className={classes.appliedFilters}
                                    scope={scope.selectedNodesPanel}
                                />
                            )}
                            <div className={classes.chartArea}>
                                <ReactECharts
                                    className={classes.chart}
                                    option={scope.options}
                                    onChartReady={handleChartReady}
                                />
                                {!scope.hasGraphData && (
                                    <div className={classes.emptyOverlay}>
                                        <LifesHubEmptyState
                                            title="Nenhum resultado para essa busca"
                                            text="Ajuste os termos ou os filtros da barra superior para desenhar as relações societárias."
                                        />
                                    </div>
                                )}
                                <HoverBarView className={classes.hoverBar} scope={scope.hoverBar} />
                            </div>
                        </LifesHubCardBody>
                    </LifesHubCard>
                )}
                {showTables && <ListagemTabsView className={classes.tablesPane} scope={scope.listagemTabs} />}
            </>
        )
    }

    return (
        <ModuleView className={className}>
            <PageView>
                {pageHead}
                {content}
            </PageView>
            <NodeMenu id={MENU_ID}>
                <Item onClick={handleExclusiveFilterClick}>Filtro exclusivo</Item>
                <Item onClick={handleAdditionalFilterClick}>Filtro adicional</Item>
                <Item onClick={handleCopyInformationalDataClick}>Copiar informações</Item>
                <Item onClick={handleCopyIdentityDataClick}>Copiar CPF/CNPJ</Item>
            </NodeMenu>
            <ConfirmExportDataLimit scope={scope.exportDataLimitScope}></ConfirmExportDataLimit>
        </ModuleView>
    )
}

// :: internals

function echartZoom(echart: ECharts, scale: number) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyEChart: any = echart
    const echartDiv: HTMLDivElement = anyEChart._dom
    const roamControler = anyEChart._chartsViews[0]._controller
    roamControler.trigger('zoom', {
        scale: scale,
        originX: (echartDiv.offsetWidth / 2) | 0,
        originY: (echartDiv.offsetHeight / 2) | 0
    })
}
